import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const InteractiveMap = () => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [hoveredRegion, setHoveredRegion] = useState(null);

  const mapBackgroundImage = "/assets/images/map.jpg";

  const regions = [
    {
      id: "everest",
      name: "Everest Region",
      description:
        "Home to the world's highest peak and legendary Sherpa culture",
      coordinates: { x: 82, y: 60 },
      treks: [
        {
          name: "Everest Base Camp",
          duration: "14 days",
          difficulty: "Moderate",
        },
        { name: "Gokyo Lakes", duration: "12 days", difficulty: "Moderate" },
        {
          name: "Three Passes",
          duration: "18 days",
          difficulty: "Challenging",
        },
      ],
      highlights: ["Everest Base Camp", "Namche Bazaar", "Tengboche Monastery"],
      bestSeason: "March-May, September-November",
      elevation: "5,364m",
      image:
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    },
    {
      id: "annapurna",
      name: "Annapurna Region",
      description:
        "Diverse landscapes from subtropical valleys to high alpine terrain",
      coordinates: { x: 49, y: 45 },
      treks: [
        {
          name: "Annapurna Circuit",
          duration: "16 days",
          difficulty: "Moderate",
        },
        {
          name: "Annapurna Base Camp",
          duration: "10 days",
          difficulty: "Easy",
        },
        {
          name: "Manaslu Circuit",
          duration: "14 days",
          difficulty: "Challenging",
        },
      ],
      highlights: ["Thorong La Pass", "Poon Hill", "Hot Springs"],
      bestSeason: "March-May, October-December",
      elevation: "4,130m",
      image:
        "https://images.pexels.com/photos/1366919/pexels-photo-1366919.jpeg?auto=compress&cs=tinysrgb&w=400&q=80",
    },
    {
      id: "langtang",
      name: "Langtang Region",
      description:
        "Sacred valleys with rich Tamang culture and pristine wilderness",
      coordinates: { x: 68, y: 53 },
      treks: [
        { name: "Langtang Valley", duration: "8 days", difficulty: "Easy" },
        {
          name: "Gosaikunda Lakes",
          duration: "10 days",
          difficulty: "Moderate",
        },
        { name: "Tamang Heritage", duration: "7 days", difficulty: "Easy" },
      ],
      highlights: ["Kyanjin Gompa", "Sacred Lakes", "Cheese Factory"],
      bestSeason: "March-May, September-November",
      elevation: "3,870m",
      image:
        "https://images.pixabay.com/photo/2017/02/08/17/24/fantasy-2049567_1280.jpg",
    },
    {
      id: "mustang",
      name: "Upper Mustang",
      description:
        "Ancient kingdom with Tibetan culture and dramatic desert landscapes",
      coordinates: { x: 48, y: 33 },
      treks: [
        { name: "Upper Mustang", duration: "12 days", difficulty: "Moderate" },
        { name: "Lo Manthang", duration: "10 days", difficulty: "Easy" },
        { name: "Muktinath Temple", duration: "6 days", difficulty: "Easy" },
      ],
      highlights: ["Lo Manthang Palace", "Sky Caves", "Ancient Monasteries"],
      bestSeason: "March-November",
      elevation: "3,840m",
      image:
        "https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    },
  ];

  const handleRegionClick = (region) => {
    setSelectedRegion(selectedRegion?.id === region?.id ? null : region);
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
            <h2 className="section-heading text-foreground mb-6">
              Explore the{" "}
              <span style={{ color: "#c4873a" }}>Himalayas</span>
            </h2>
          <p className="font-body text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto">
            Discover diverse regions, each offering unique landscapes, cultures,
            and adventures. Click on any region to explore available treks and
            cultural experiences.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Interactive Map */}
          <div className="lg:col-span-2">
            <div className="relative sm:p-8 overflow-hidden">
              {/* Map Background */}
              <div
                className="relative w-full aspect-[2000/1154] overflow-hidden"
                style={{
                  backgroundImage: `url(${mapBackgroundImage})`,
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {/* Region Markers */}
                {regions?.map((region) => (
                  <div
                    key={region?.id}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                    style={{
                      left: `${region?.coordinates?.x}%`,
                      top: `${region?.coordinates?.y}%`,
                    }}
                    onClick={() => handleRegionClick(region)}
                    onMouseEnter={() => setHoveredRegion(region)}
                    onMouseLeave={() => setHoveredRegion(null)}
                  >
                    {/* Marker */}
                    <div
                      className={`w-6 h-6 rounded-full border-4 border-white shadow-lg transition-all duration-300 ${
                        selectedRegion?.id === region?.id
                          ? "bg-conversion-cta scale-150"
                          : hoveredRegion?.id === region?.id
                          ? "bg-accent scale-125"
                          : "bg-primary hover:scale-110"
                      }`}
                    >
                      <div className="absolute inset-0 rounded-full animate-ping bg-current opacity-20"></div>
                    </div>

                    {/* Region Label */}
                    <div
                      className={`absolute top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap transition-all duration-300 ${
                        hoveredRegion?.id === region?.id ||
                        selectedRegion?.id === region?.id
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-2"
                      }`}
                    >
                      <div className="bg-white px-3 py-1 rounded-lg shadow-lg border border-border">
                        <span className="font-body text-sm font-medium text-foreground">
                          {region?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Map Legend */}
                <div className="absolute top-4 left-88 bg-white/90 backdrop-blur-sm rounded-lg p-4 border border-border">
                  <h4 className="font-headline text-sm font-bold text-foreground mb-2">
                    Himalayan Regions
                  </h4>
                  <div className="space-y-1">
                    <div className="flex items-center text-xs text-text-secondary">
                      <div className="w-3 h-3 bg-primary rounded-full mr-2"></div>
                      Available Regions
                    </div>
                    <div className="flex items-center text-xs text-text-secondary">
                      <div className="w-3 h-3 bg-accent rounded-full mr-2"></div>
                      Hover to Preview
                    </div>
                    <div className="flex items-center text-xs text-text-secondary">
                      <div className="w-3 h-3 bg-conversion-cta rounded-full mr-2"></div>
                      Selected Region
                    </div>
                  </div>
                </div>

                {/* Compass */}
                <div className="absolute top-4 right-4 w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full border border-border flex items-center justify-center">
                  <Icon name="Compass" size={24} className="text-primary" />
                </div>
              </div>
            </div>
          </div>

          {/* Region Details Panel */}
          <div className="lg:col-span-1">
            {selectedRegion ? (
              <div className="bg-white rounded-2xl card-elevation p-6 animate-scale-in">
                {/* Region Header */}
                <div className="mb-6">
                  <h3 className="font-headline text-2xl text-foreground mb-2" style={{ fontWeight: 300 }}>
                    {selectedRegion?.name}
                  </h3>
                  <p className="font-body text-text-secondary">
                    {selectedRegion?.description}
                  </p>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-3 bg-surface rounded-lg">
                    <div className="font-headline text-lg font-bold text-primary">
                      {selectedRegion?.elevation}
                    </div>
                    <div className="font-body text-xs text-text-secondary">
                      Max Elevation
                    </div>
                  </div>
                  <div className="text-center p-3 bg-surface rounded-lg">
                    <div className="font-headline text-lg font-bold text-primary">
                      {selectedRegion?.treks?.length}
                    </div>
                    <div className="font-body text-xs text-text-secondary">
                      Trek Options
                    </div>
                  </div>
                </div>

                {/* Best Season */}
                <div className="mb-6">
                  <h4 className="font-headline text-sm text-foreground mb-2" style={{ fontWeight: 300 }}>
                    Best Season
                  </h4>
                  <div className="flex items-center">
                    <Icon
                      name="Calendar"
                      size={16}
                      className="text-primary mr-2"
                    />
                    <span className="font-body text-sm text-text-secondary">
                      {selectedRegion?.bestSeason}
                    </span>
                  </div>
                </div>

                {/* Available Treks */}
                <div className="mb-6">
                  <h4 className="font-headline text-sm text-foreground mb-3" style={{ fontWeight: 300 }}>
                    Popular Treks
                  </h4>
                  <div className="space-y-2">
                    {selectedRegion?.treks?.map((trek, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-surface rounded-lg"
                      >
                        <div>
                          <div className="font-body text-sm font-medium text-foreground">
                            {trek?.name}
                          </div>
                          <div className="font-body text-xs text-text-secondary">
                            {trek?.duration} • {trek?.difficulty}
                          </div>
                        </div>
                        <Icon
                          name="ChevronRight"
                          size={16}
                          className="text-text-secondary"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Highlights */}
                <div className="mb-6">
                  <h4 className="font-headline text-sm text-foreground mb-3" style={{ fontWeight: 300 }}>
                    Key Highlights
                  </h4>
                  <div className="space-y-2">
                    {selectedRegion?.highlights?.map((highlight, index) => (
                      <div key={index} className="flex items-center">
                        <Icon
                          name="MapPin"
                          size={14}
                          className="text-primary mr-2 flex-shrink-0"
                        />
                        <span className="font-body text-sm text-text-secondary">
                          {highlight}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <Button
                  variant="default"
                  size="default"
                  fullWidth
                  className="btn-primary font-body"
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Explore {selectedRegion?.name}
                </Button>
              </div>
            ) : (
              <div className="bg-surface rounded-2xl p-8 text-center">
                <div className="w-16 h-16 mountain-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Mountain" size={32} color="white" />
                </div>
                <h3 className="font-headline text-xl text-foreground mb-2" style={{ fontWeight: 300 }}>
                  Select a Region
                </h3>
                <p className="font-body text-text-secondary mb-6">
                  Click on any region marker to explore available treks,
                  cultural highlights, and detailed information.
                </p>
                <div className="space-y-2">
                  {regions?.map((region) => (
                    <button
                      key={region?.id}
                      onClick={() => handleRegionClick(region)}
                      className="w-full text-left p-3 rounded-lg hover:bg-white transition-colors duration-200 flex items-center justify-between"
                    >
                      <span className="font-body text-sm text-foreground">
                        {region?.name}
                      </span>
                      <Icon
                        name="ChevronRight"
                        size={16}
                        className="text-text-secondary"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveMap;
